import { useMemo } from 'react'
import { getUserProjects, getUserRoles } from '../../../utils/SessionStorageUtils'
import { InvestorType } from '../accounts/components/AddInvestor'

type actions = 'create' | 'read' | 'update' | 'delete' | '*'

type resources =
  | 'documentAssembler'
  | 'portfolio'
  | 'dashboard'
  | 'watchlist'
  | 'company'
  | 'indirectCompanies'
  | 'adduser'
  | 'addInvestor'
  | 'addAccount'
  | 'companyAccess'
  | 'exitEnabler'
  | 'exitEnablerAdmin'
  | 'myCaptables'
  | 'dynamicTable'
  | 'project'
  | 'dealFlow'
  | 'esopModule'
  | '*'

export interface Role {
  resourceName?: string
  resource: resources
  actionName?: string
  action: actions
  attributes: string[]
}

export interface UserInvestor {
  investorName: string
  type: InvestorType
  investorId: number
}

const exclusionRoles = ['companyAccess', 'exitEnabler', 'exitEnablerAdmin', 'dynamicTable']

const checkResourceInExclusionList = (resources: string[]) => {
  return resources.some((res) => exclusionRoles.includes(res))
}

export function useCanUserAccess(action: actions | actions[], resource: resources | resources[]) {
  const data = getUserRoles('userRoles')

  const canUserAccess = useMemo(() => {
    const userRoles = data || []
    const newPermissions = new Set<Role>()
    const higherPermissions: actions[] = ['create', 'delete', 'update']

    userRoles?.forEach((role) => {
      if (higherPermissions.includes(role.action)) {
        newPermissions.add({ ...role, action: 'read' })
      }
    })

    userRoles.concat(...Array.from(newPermissions))

    if (checkResourceInExclusionList(Array.isArray(resource) ? resource : [resource])) {
      const canAccessResource = userRoles.filter((res) => resource.includes(res.resource))
      if (canAccessResource.length === 0) return false
      const canPerformAction = canAccessResource.find(
        (res) => action.includes(res.action) || res.action === '*'
      )
      return canPerformAction
    }

    const isAdmin = userRoles?.some((role) => role.resource === '*' && role.action === '*')

    if (isAdmin) return true

    let canAccessResource = userRoles?.some(
      (role) => role.resource === resource || role.resource === '*'
    )

    let canPerformAction = userRoles?.some(
      (role) => role.action === action && (resource === role.resource || role.resource === '*')
    )

    if (canAccessResource && !canPerformAction) {
      canPerformAction = userRoles?.some(
        (role) => role.resource === resource && role.action === '*'
      )
    }

    if (canPerformAction && !canAccessResource) {
      canAccessResource = userRoles?.some((role) => role.resource === '*' && role.action === action)
    }

    return canAccessResource && canPerformAction
  }, [data, action, resource])
  if (resource === 'project') {
    // warning: hardcoded project value change if name is changed in task manager
    const projects = (getUserProjects('userProjects') || []).filter(
      (project) => !project.type?.toLowerCase().includes("dealflow")
    )
    if (projects.length === 0) {
      return false
    }
    return true
  } else if (resource === 'dealFlow') {
    const projects = (getUserProjects('userProjects') || []).filter(
      (project) => project.type?.toLowerCase().includes("dealflow")
    )
    if (projects.length === 0) {
      return false
    }
    return true
  }
  // for esop
  // else if (resource === 'dealFlow') {
  //   const projects = (getUserProjects('userProjects') || []).filter(project => project.type === 'Deal Flow')
  //   if (projects.length === 0 ) {
  //     return false
  //   }
  //   return true
  // }
  return canUserAccess
}

export const isSuperAdmin = () => {
  return sessionStorage.getItem('isSuperAdmin') === 'true' ? true : false
}
